import { Table } from '@statsbomb/kitbag-components';
import { useAtom, useSetAtom } from 'jotai';
import { PropTypes } from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled } from 'styled-components';
import { sourceDataIsUpdatingAtom } from '../../atoms/approval';
import { editingSourceDataAtom } from '../../atoms/review';
import { selectedSourceDataAtom } from '../../atoms/selectedSourceData';
import useApi from '../../hooks/api';
import BooleanDisplay from '../BooleanDisplay';
import { EditButton } from '../EditButton/EditButton';
import { SelectSourceName } from '../SelectSource/SelectSourceName/SelectSourceName';
import { SelectSourceType } from '../SelectSource/SelectSourceType/SelectSourceType';
import SelectUploadMethod from '../SelectSource/SelectUploadMethod';
import { getUploadMethods } from '../SelectSource/sourceOptions';
import {
  getDisplayLabelFromSourceName,
  getDisplayLabelFromSourceType,
} from '../SelectSource/displayUtils';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

export function SourceData({
  editable,
  lastUpdatedBy,
  lastUpdatedByIsHidden,
  onSave,
  sourceData,
  sport,
}) {
  const intl = useIntl();
  const api = useApi();

  const setSelectedSourceData = useSetAtom(selectedSourceDataAtom);
  const [sourceDataIsUpdating, setSourceDataIsUpdating] = useAtom(
    sourceDataIsUpdatingAtom,
  );

  const [isEditing, setIsEditing] = useAtom(editingSourceDataAtom);

  const UNKNOWN = intl.formatMessage({
    defaultMessage: 'Unknown',
    id: '88183',
  });

  const uploadMethodSelectedValue = getUploadMethods(intl).find(
    (method) => method.value.id === sourceData.uploadMethod,
  );
  const uploadMethodDisplayValue = uploadMethodSelectedValue?.label;

  const fallback = intl.formatMessage({
    defaultMessage: 'Unknown',
    id: '88183',
  });

  const sourceTypeDisplayValue =
    getDisplayLabelFromSourceType(intl, sourceData.sourceType) || fallback;

  const sourceNameDisplayValue =
    getDisplayLabelFromSourceName(intl, sourceData.sourceName) ||
    sourceData.sourceName ||
    fallback;

  const formSubmit = async () => {
    setSourceDataIsUpdating(true);
    await onSave();
    setSourceDataIsUpdating(false);
  };

  const handleEditClick = (editing) => {
    if (editing) {
      setSelectedSourceData(sourceData);
    }
    setIsEditing(editing);
  };

  return (
    <Table>
      <Table.Head>
        <Table.HeaderCell colSpan={2}>
          <Container>
            <FormattedMessage defaultMessage="Source Data" id="9c204" />
            {editable && !uploadMethodSelectedValue?.preventManualSelection && (
              <EditButton
                editing={isEditing}
                editTitle={intl.formatMessage({
                  defaultMessage: 'Edit Source Data',
                  id: '32264',
                })}
                isPending={sourceDataIsUpdating}
                onEdit={handleEditClick}
                onSave={formSubmit}
                saveDisabled={api.isFetching}
              />
            )}
          </Container>
        </Table.HeaderCell>
      </Table.Head>
      <Table.Body>
        {!lastUpdatedByIsHidden && (
          <Table.Row>
            <Table.DataCell $textAlign="left">
              <FormattedMessage defaultMessage="Last Updated By" id="7ce1b" />
            </Table.DataCell>
            <Table.DataCell $textAlign="left">
              {lastUpdatedBy || '-'}
            </Table.DataCell>
          </Table.Row>
        )}
        <Table.Row>
          <Table.DataCell $textAlign="left">
            <FormattedMessage defaultMessage="Upload method" id="adb20" />
          </Table.DataCell>
          <Table.DataCell $textAlign="left">
            {isEditing ? (
              <SelectUploadMethod size="tiny" />
            ) : (
              uploadMethodDisplayValue || UNKNOWN
            )}
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell $textAlign="left">
            <FormattedMessage defaultMessage="Source Type" id="7542d" />
          </Table.DataCell>
          <Table.DataCell $textAlign="left">
            {isEditing ? (
              <SelectSourceType size="tiny" />
            ) : (
              sourceTypeDisplayValue || UNKNOWN
            )}
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell $textAlign="left">
            <FormattedMessage defaultMessage="Source Name" id="9d58d" />
          </Table.DataCell>
          <Table.DataCell $textAlign="left">
            {isEditing ? (
              <SelectSourceName size="tiny" sport={sport} />
            ) : (
              sourceNameDisplayValue || sourceData.sourceName || UNKNOWN
            )}
          </Table.DataCell>
        </Table.Row>
        <Table.Row>
          <Table.DataCell $textAlign="left">
            <FormattedMessage defaultMessage="Is Licensed" id="9ab77" />
          </Table.DataCell>
          <Table.DataCell $textAlign="left">
            <BooleanDisplay value={sourceData?.isLicensed} />
          </Table.DataCell>
        </Table.Row>
      </Table.Body>
    </Table>
  );
}

SourceData.propTypes = {
  editable: PropTypes.bool,
  lastUpdatedBy: PropTypes.string,
  lastUpdatedByIsHidden: PropTypes.bool,
  onSave: PropTypes.func,
  sourceData: PropTypes.shape({
    isLicensed: PropTypes.string,
    sourceName: PropTypes.string,
    sourceType: PropTypes.string,
    uploadMethod: PropTypes.string,
  }).isRequired,
  sport: PropTypes.string,
};

SourceData.defaultProps = {
  editable: false,
  lastUpdatedBy: '-',
  lastUpdatedByIsHidden: false,
  onSave: () => {},
  sport: undefined,
};

export default { SourceData };
